import { useForm } from 'react-hook-form';
import Input from 'components/Common/Input';
import Button from 'components/Common/Button';
import Select from 'components/Common/Select';
import { Box, Grid2, Typography, Drawer, IconButton } from '@mui/material';
import { useGetAgentLanguages } from 'apis/business/agent/languages/hook';
import { VoiceDetailsFormValues } from './types';
import { IoClose } from 'react-icons/io5';

interface VoiceDetailsFormProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (data: VoiceDetailsFormValues) => void;
  readonly isPending?: boolean;
}

export default function VoiceDetailsForm({ open, onClose, onSubmit, isPending = false }: VoiceDetailsFormProps) {
  const { control, handleSubmit } = useForm<VoiceDetailsFormValues>();
  const { data: languagesData, isLoading: isLanguagesLoading } = useGetAgentLanguages();
  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400, md: 600 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 100001
        }
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid #DEDEDE',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}
      >
        <Typography variant='h6'>Add Voice Details</Typography>
        <IconButton onClick={onClose} size='small'>
          <IoClose size={24} />
        </IconButton>
      </Box>

      {/* Form with Fixed Footer */}
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Scrollable Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 3
          }}
        >
          <Grid2 container spacing='26px'>
            <Grid2 size={12}>
              <Input name='name' label='Voice Name' control={control} fullWidth required />
            </Grid2>
            <Grid2 size={12}>
              <Select
                disabled={isLanguagesLoading}
                required
                name='language'
                control={control}
                label='Language'
                options={languagesData?.languages || []}
              />
            </Grid2>
            <Grid2 size={12}>
              <Select
                name='gender'
                required
                control={control}
                label='Gender'
                options={[
                  { label: 'Male', value: 'male' },
                  { label: 'Female', value: 'female' },
                  { label: 'Neutral', value: 'neutral' }
                ]}
              />
            </Grid2>
          </Grid2>
        </Box>

        {/* Fixed Footer */}
        <Box
          sx={{
            p: 3,
            borderTop: '1px solid #DEDEDE',
            display: 'flex',
            justifyContent: 'end',
            gap: '16px',
            flexShrink: 0
          }}
        >
          <Button color='secondary' variant='outlined' sx={{ border: '1px solid #F3F3F3' }} onClick={onClose}>
            Cancel
          </Button>
          <Button type='submit' variant='contained' isPending={isPending}>
            Submit
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
